import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { CampaignsContext } from "contexts/Campaigns";
import { SavedBrandCollectionsContext } from "contexts/SavedBrandCollections";
import { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Campaign, CampaignStats } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { formatCampaignDate } from "utils/time";

import { CampaignStatusChip } from "../CampaignStatusChip";

interface IProps {
  campaign: Campaign;
  stats?: CampaignStats;
  statsLoading: boolean;
  isMobileScreen: boolean;
}

export default function CampaignCard({
  campaign,
  stats,
  statsLoading,
  isMobileScreen,
}: IProps) {
  const { deleteCampaign } = useContext(CampaignsContext);
  const { collections } = useContext(SavedBrandCollectionsContext);
  const { categoriesById } = useContext(BentoCategoriesContext);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] =
    useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const collectionsLength = Object.keys(collections)?.length;
  const categoriesLength = Object.keys(categoriesById)?.length;

  const campaignTags = useMemo(() => {
    const tags = [];
    if (campaign?.createdByAdmin) {
      return ["Bento AI 🪄"];
    } else if (campaign.savedBrandCollectionId) {
      tags.push(
        collections[campaign.savedBrandCollectionId]?.name?.toLowerCase(),
      );
    }
    if (campaign.isAllSavedCollection) {
      tags.push("all saved collection");
    }
    if (
      campaign.searchParameters?.category_ids &&
      campaign.searchParameters.category_ids.length > 0
    ) {
      const categories = campaign.searchParameters.category_ids.slice(0, 3);
      for (const categoryId of categories) {
        tags.push(
          categoriesById[parseInt(categoryId)]?.displayName?.toLowerCase(),
        );
      }
    } else if (campaign.searchParameters) {
      tags.push("brand search");
    }

    return tags.filter((tag) => !!tag);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionsLength, categoriesLength, campaign.id]);

  const handleConfirm = async () => {
    setDeleteLoading(true);
    await deleteCampaign(campaign.id);
    setConfirmDeleteDialogOpen(false);
    setDeleteLoading(false);
  };

  return (
    <Card variant="outlined">
      <CardActionArea component={Link} to={`campaign/${campaign.id}`}>
        <CardContent>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            rowGap={1}
          >
            <Grid
              item
              xs={12}
              container
              alignItems="center"
              sm
              rowGap={1}
              sx={{ mb: { xs: 2 } }}
            >
              <Grid item xs={12}>
                <Typography variant="h6">{campaign.name}</Typography>
                <Typography>
                  {formatCampaignDate(campaign.startDate)} to{" "}
                  {formatCampaignDate(campaign.endDate)}
                </Typography>
              </Grid>
              <Grid item container alignItems="center" gap={1} xs={12}>
                <CampaignStatusChip
                  status={campaign.status}
                  startDate={campaign?.startDate}
                />
                {campaignTags.map((tag) => (
                  <Chip
                    size="small"
                    label={tag}
                    key={tag}
                    sx={{
                      backgroundColor: "secondary.light",
                      fontSize: 10,
                    }}
                  />
                ))}
              </Grid>
            </Grid>
            {isMobileScreen && (
              <Grid container item xs={12} justifyContent="space-between">
                <Grid item xs={2} sx={{ textAlign: "center" }}>
                  <Typography color="textSecondary" sx={{ fontSize: 9 }}>
                    Brands
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={{ textAlign: "center" }}>
                  <Typography color="textSecondary" sx={{ fontSize: 9 }}>
                    Sent
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={{ textAlign: "center" }}>
                  <Typography color="textSecondary" sx={{ fontSize: 9 }}>
                    Opens
                  </Typography>
                </Grid>
                <Grid item xs={2} sx={{ textAlign: "center" }}>
                  <Typography color="textSecondary" sx={{ fontSize: 9 }}>
                    Responses
                  </Typography>
                </Grid>
                <Grid item xs={2}></Grid>
              </Grid>
            )}
            <Grid item xs={2} sx={{ textAlign: "center" }}>
              {campaign.campaignBrandCount}
            </Grid>
            <Grid item xs={2} sx={{ textAlign: "center" }}>
              {statsLoading ? "-" : stats?.outreachCount}
            </Grid>
            <Grid item xs={2} sx={{ textAlign: "center" }}>
              {statsLoading
                ? "-"
                : `${stats?.openedCount} (${(
                    (stats?.openedPercentage || 0) * 100
                  ).toFixed(2)}%)`}
            </Grid>
            <Grid item xs={2} sx={{ textAlign: "center" }}>
              {statsLoading
                ? "-"
                : `${stats?.respondedCount} (${(
                    (stats?.respondedPercentage || 0) * 100
                  ).toFixed(2)}%)`}
            </Grid>
            <Grid container item xs={2} gap={1} justifyContent="center">
              <IconButton
                size="small"
                sx={{ color: grey[500] }}
                onClick={(e: CustomEvent) => {
                  e.preventDefault();
                  setConfirmDeleteDialogOpen(true);
                }}
              >
                <Box component="span" className="fa-solid fa-trash" />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </CardActionArea>

      <ConfirmDialog
        header={`Delete Campaign`}
        subheader={`Are you sure you want to delete campaign "${campaign.name}"?`}
        open={confirmDeleteDialogOpen}
        handleClose={() => setConfirmDeleteDialogOpen(false)}
        handleConfirm={handleConfirm}
        loading={deleteLoading}
      />
    </Card>
  );
}
